import CircularProgress from "@mui/material/CircularProgress";
import { ReactComponent as PlusIcon } from "assets/icons/plus.svg";
import AddAchCard from "components/addAchCard/AddAchCard";
import MyModal from "components/MyModal";
import VerifyAchCardInfo from "components/verifyAchCardInfo/VerifyAchCardInfo";
import { checkIsStripeByPaymentGateway } from "helper/checkIsStripe";
import { loadStripe } from "@stripe/stripe-js";
import FinanceService from "Lib/API/Finance";
import { useSnackbar } from "notistack";
import React, { useEffect, useMemo, useState } from "react";
import { toastr } from "react-redux-toastr";
import { getAccessTokenSilently, getLocalStorageItem } from "utils/functions";
import Button from "components/button/Button";
import { Fonts, FontColors } from "theme/fontConfig";

const ManageAchCards = ({ setSelectedAchCard, selectedAchCard }) => {
  const { enqueueSnackbar } = useSnackbar();

  const accountID = localStorage.getItem("accountID") || "";
  const enableAchPay = getLocalStorageItem("paymentMethodAch", true);
  const firstName = getLocalStorageItem("accountInfo", true)?.firstName;
  const lastName = getLocalStorageItem("accountInfo", true)?.lastName;
  const email = getLocalStorageItem("accountInfo", true)?.primary_email_address;

  const isStripe = useMemo(
    () => checkIsStripeByPaymentGateway(enableAchPay?.paymentGateway),
    [enableAchPay]
  );

  const [stripePromise, setStripePromise] = useState(null);
  const [cardLoading, setCardLoading] = useState(false);
  const [cardList, setCardList] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [openVerifyModal, setOpenVerifyModal] = useState(false);
  const [achSetupLoader, setAchSetupLoader] = useState(false);

  const [verifyData, setVerifyData] = useState({
    achNumberOne: "",
    achNumberTwo: "",
    achSM: "",
  });
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyError, setVerifyError] = useState(null);

  useEffect(() => {
    const key = enableAchPay?.paymentGateway?.keys?.publicKey;
    if (key) setStripePromise(loadStripe(key));
  }, []);

  const fetchAchCards = async () => {
    setCardLoading(true);
    const token = localStorage.getItem("token");
    await FinanceService.getACHCardsByAccountId(token, accountID)
      .then((res) => {
        setCardList(res?.ach?.data || []);
        if (res?.ach?.data?.length > 0 && !selectedAchCard) {
          const firstVerified = res?.ach?.data?.find((c) => c.status === "verified");
          setSelectedAchCard(firstVerified?.id || "");
        }
      })
      .catch((err) => {
        enqueueSnackbar(err?.data?.message, { variant: "error" });
      })
      .finally(() => {
        setCardLoading(false);
      });
  };

  useEffect(() => {
    if (!accountID) return;
    fetchAchCards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountID]);

  const onVerifyAchChange = (e) => {
    const { value, name } = e.target;
    setVerifyData((prev) => ({ ...prev, [name]: value }));
  };

  const isVerifyBtnDisabled = useMemo(() => {
    return verifyLoading || !(
      (verifyData?.achNumberOne && verifyData?.achNumberTwo && !verifyData?.achSM) ||
      (!verifyData?.achNumberOne && !verifyData?.achNumberTwo && verifyData?.achSM)
    );
  }, [verifyData, verifyLoading]);

  const handleVerifyAch = async (item) => {
    setVerifyLoading(true);
    const data = {
      accountId: accountID,
      achId: item?.id,
      firstAmount: verifyData?.achNumberOne || null,
      secondAmount: verifyData?.achNumberTwo || null,
      smCode: verifyData?.achSM || null,
    };
    if (data.firstAmount === null) delete data.firstAmount;
    if (data.secondAmount === null) delete data.secondAmount;
    if (data.smCode === null) delete data.smCode;
    const token = await getAccessTokenSilently();
    FinanceService.verifyAch(data, token)
      .then(() => {
        fetchAchCards();
        toastr.success("Success", "ACH account verified successfully");
      })
      .catch((err) => {
        setVerifyError(err?.data?.message);
        toastr.error("Error", err?.data?.message ?? "Please enter valid details");
        setTimeout(() => setVerifyError(null), 7000);
      })
      .finally(() => {
        setVerifyLoading(false);
      });
  };

  const handleCheckStripeSetup = async () => {
    if (!isStripe) {
      setOpenModal(true);
      return;
    }

    if (!stripePromise) {
      toastr.error("Error", "Stripe is still loading. Please try again.");
      return;
    }

    setAchSetupLoader(true);
    const token = await getAccessTokenSilently();
    const stripe = await stripePromise;
    await FinanceService.checkStripeSetup(token)
      .then(async (res) => {
        const { error: collectError } = await stripe.collectBankAccountForSetup({
          clientSecret: res?.paymentIntent?.client_secret,
          params: {
            payment_method_type: "us_bank_account",
            payment_method_data: {
              billing_details: {
                name: `${firstName || ""} ${lastName || ""}`.trim(),
                email: email || undefined,
              },
            },
          },
        });
        if (!collectError) {
          const { setupIntent, error: confirmError } =
            await stripe.confirmUsBankAccountSetup(
              res?.paymentIntent?.client_secret
            );
          if (setupIntent) {
            await FinanceService.addACHAccount(token, accountID, setupIntent)
              .then(() => {
                toastr.success("Success", "ACH Added Successfully");
                fetchAchCards();
              })
              .catch((err) => {
                toastr.error("Error", err?.data?.message || "Something went wrong");
              });
          }
          if (confirmError) {
            toastr.error("Error", confirmError.message || "Something went wrong during confirmation");
          }
        }
      })
      .catch((err) => {
        toastr.error("Error", err?.data?.message || "Something went wrong");
      });
    setAchSetupLoader(false);
  };

  return (
    <div className="card_num_field" style={{ width: "100%" }}>
      {cardLoading ? (
        <div className="text-center">
          <CircularProgress />
        </div>
      ) : (
        <>
          {cardList?.length > 0 && (
            <div
              className="cards-radio font-16 d-flex flex-column items-center"
              style={{ gap: "10px", width: "100%", marginBottom: 12 }}
            >
              {cardList.map((item, index) => (
                <div key={item.id} style={{ width: "100%" }}>
                  <div
                    data-id={item.id}
                    onClick={() =>
                      item.status === "verified"
                        ? setSelectedAchCard(selectedAchCard === item.id ? "" : item.id)
                        : undefined
                    }
                    className={selectedAchCard === item.id ? "selected-card" : ""}
                    style={{ width: "100%", opacity: item.status !== "verified" ? 0.6 : 1 }}
                  >
                    <p className="justify-content-between">
                      {`${item.nameOnAccount || ""} ${item.accountNumber ? "/ " + item.accountNumber : ""}`}
                      <div
                        className="checkbox checkbox-lg checkbox-single"
                        style={{ margin: "0 !important" }}
                      >
                        <input
                          type="checkbox"
                          name={`card_number${index}`}
                          id={index}
                          onChange={(e) =>
                            item.status === "verified"
                              ? setSelectedAchCard(e.target?.checked ? item?.id : "")
                              : undefined
                          }
                          checked={selectedAchCard === item?.id}
                          disabled={item.status !== "verified"}
                        />
                        <span />
                      </div>
                    </p>
                  </div>

                  {item.status !== "verified" && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 4, paddingLeft: 4 }}>
                      <span
                        style={{ cursor: "pointer", fontWeight: Fonts.GeneralSubHeaderFont.fontWeight, fontSize: Fonts.GeneralSubHeaderFont.fontSize, borderRadius: "50%", border: "1px solid #ccc", width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center", background: "#f5f5f5", flexShrink: 0 }}
                        title="How to verify?"
                        onClick={() => setOpenVerifyModal(true)}
                      >?</span>
                      <input
                        type="text"
                        maxLength={2}
                        name="achNumberOne"
                        onChange={onVerifyAchChange}
                        style={{ width: 40, height: 32, border: "0.9px solid gray", textAlign: "center", borderRadius: 5 }}
                      />
                      <input
                        type="text"
                        maxLength={2}
                        name="achNumberTwo"
                        onChange={onVerifyAchChange}
                        style={{ width: 40, height: 32, border: "0.9px solid gray", textAlign: "center", borderRadius: 5 }}
                      />
                      <span style={{ fontSize: Fonts.GeneralSmallBodyFont.fontSize }}>OR</span>
                      <input
                        type="text"
                        maxLength={6}
                        minLength={6}
                        name="achSM"
                        onChange={onVerifyAchChange}
                        style={{ width: 120, height: 32, border: "0.9px solid gray", textAlign: "center", borderRadius: 5 }}
                      />
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => handleVerifyAch(item)}
                        disabled={isVerifyBtnDisabled}
                        style={{ minWidth: 70 }}
                      >
                        {verifyLoading ? (
                          <CircularProgress size={16} style={{ color: FontColors.White }} />
                        ) : (
                          "Verify"
                        )}
                      </Button>
                      {verifyError && (
                        <span style={{ fontSize: Fonts.GeneralSmallBodyFont.fontSize, color: FontColors.Red, width: "100%" }}>{verifyError}</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <Button
            variant="primary"
            size="md"
            onClick={handleCheckStripeSetup}
            disabled={achSetupLoader}
            style={{ minWidth: 165 }}
          >
            {achSetupLoader ? (
              <CircularProgress size={18} style={{ color: FontColors.White }} />
            ) : (
              <PlusIcon />
            )}
            <span>Add New ACH</span>
          </Button>
        </>
      )}

      <MyModal
        className="add-card-modal"
        open={openModal}
        onClose={() => setOpenModal(false)}
      >
        <AddAchCard
          setOpenModal={setOpenModal}
          refetchAchCards={fetchAchCards}
          closeModal={() => setOpenModal(false)}
          openVerifyInfoModal={() => setOpenVerifyModal(true)}
          bottomAdd
        />
      </MyModal>

      <MyModal
        className="main__alert-dialog"
        open={openVerifyModal}
        onClose={() => setOpenVerifyModal(false)}
      >
        <VerifyAchCardInfo closeModal={() => setOpenVerifyModal(false)} />
      </MyModal>
    </div>
  );
};

export default ManageAchCards;
